import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScroll, useTransform, useMotionValueEvent, motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGS, LANG_LABELS, subscribeAll, menuView, pick } from '../menu/model';
import { Tile, mixTheme, Rosette, Glass } from './illustrations';
import { sound } from './sound';
import { isBordo } from '../theme';
import ThemeToggle from '../ThemeToggle';
import Logo from '../Logo';
import Reservation from './Reservation';
import { PrintSheet } from '../menu/MenuAdmin';
import '../menu/menu.css';
import './experience.css';


// Chapter names in Turkish and Spanish, the two voices of this table.
const CH = {
  table: ['Menü', 'La Carta'],
  bar: ['Bar', 'La Barra'],
  night: ['Gece Yarısı', 'Medianoche'],
};

const T = {
  tr: {
    tagline: 'Arkadaşlar için kurulan sofralar, günün menüleri.',
    book: 'Rezervasyon', menu: 'Menü', scroll: 'Kaydır',
    tableH: 'Bu akşamın menüsü',
    barH: 'Bir kadeh daha?',
    nightH: 'Sofra toplanır, sohbet sürer.', nightCta: 'Menüye bak',
    soundOn: 'Ses açık', soundOff: 'Ses kapalı',
  },
  en: {
    tagline: 'Tables set for friends, menus of the day.',
    book: 'Book', menu: 'Menu', scroll: 'Scroll',
    tableH: 'Tonight’s menu',
    barH: 'One more glass?',
    nightH: 'The plates are cleared, the talk goes on.', nightCta: 'See the menu',
    soundOn: 'Sound on', soundOff: 'Sound off',
  },
  fr: {
    tagline: 'Des tables dressées pour les amis, les menus du jour.',
    book: 'Réserver', menu: 'Carte', scroll: 'Défiler',
    tableH: 'La carte du soir',
    barH: 'Encore un verre ?',
    nightH: 'On débarrasse, la conversation continue.', nightCta: 'Voir la carte',
    soundOn: 'Son activé', soundOff: 'Son coupé',
  },
  es: {
    tagline: 'Mesas puestas para amigos, menús del día.',
    book: 'Reservar', menu: 'Carta', scroll: 'Desliza',
    tableH: 'La carta de esta noche',
    barH: '¿Otra copa?',
    nightH: 'Se recoge la mesa, la charla sigue.', nightCta: 'Ver la carta',
    soundOn: 'Sonido activado', soundOff: 'Sonido apagado',
  },
};

const SCENES = ['dusk', 'table', 'bar', 'night'];
// Warm and light the whole way down: bone, sand, deeper sand, bone.
const SKY_LIGHT = ['#f4efe6', '#f3eadb', '#f2e7d6', '#f4efe6'];
const SKY_BORDO = ['#1d1819', '#231b1d', '#2a1f23', '#1d1819'];

const Chapter = ({ id, time }) => (
  <p className="ex-chapter">
    <span className="ex-time">{time}</span>
    {CH[id].map((n, i) => <span key={n} className={i ? 'ex-chapter-alt' : ''}>{n}</span>)}
  </p>
);

// ----- the tile wall: the opening 3D moment -----

const pickTheme = (r, c, rows, cols) => {
  const cr = (rows - 1) / 2;
  const cc = (cols - 1) / 2;
  if (Math.abs(r - cr) < 0.6 || Math.abs(c - cc) < 0.6) return 'oxblood';
  return mixTheme(r * cols + c);
};

const TileWall = () => {
  const wall = useRef(null);
  const [grid, setGrid] = useState({ rows: 6, cols: 9 });

  useEffect(() => {
    const fit = () => {
      const size = window.innerWidth < 600 ? 84 : 128;
      setGrid({ cols: Math.ceil(window.innerWidth / size) + 1, rows: Math.ceil(window.innerHeight / size) + 1 });
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // The whole wall leans toward the pointer.
  useEffect(() => {
    const el = wall.current;
    const onMove = (e) => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      if (el) el.style.transform = `rotateX(${(-y * 8).toFixed(2)}deg) rotateY(${(x * 10).toFixed(2)}deg)`;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  const { rows, cols } = grid;
  const tiles = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const dist = Math.hypot(r - (rows - 1) / 2, c - (cols - 1) / 2);
      tiles.push(
        <div key={`${r}-${c}`} className="ex-tile" style={{ animationDelay: `${0.25 + dist * 0.11}s` }}>
          <div className="ex-tile-face ex-tile-front" />
          <div className="ex-tile-face ex-tile-back"><Tile theme={pickTheme(r, c, rows, cols)} /></div>
        </div>,
      );
    }
  }
  return (
    <div className="ex-wall-stage" aria-hidden="true">
      <div className="ex-wall" ref={wall} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>{tiles}</div>
    </div>
  );
};


// A frieze of tiles that flips into place when scrolled to.
const Band = ({ seed = 0 }) => (
  <div className="ex-band" data-reveal aria-hidden="true">
    {Array.from({ length: 18 }).map((_, i) => (
      <div key={i} style={{ transitionDelay: `${i * 45}ms` }}>
        <Tile theme={mixTheme(i + seed * 40)} />
      </div>
    ))}
  </div>
);

// Adds .is-in once an element scrolls into view (CSS does the motion).
const useReveal = (deps) => {
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-in'); });
    }, { threshold: 0.15 });
    document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

const Experience = () => {
  const { currentLanguage: lang, changeLanguage } = useLanguage();
  const t = T[lang] || T.en;
  const [soundOn, setSoundOn] = useState(false);
  const [data, setData] = useState(null);
  const [scene, setScene] = useState('dusk');
  const clockRef = useRef(null);

  useEffect(() => {
    // The evening always starts at 19:00, not where the last visit stopped.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    return subscribeAll(setData, () => {});
  }, []);
  useReveal([data]);

  const { scrollYProgress } = useScroll();
  const sky = useTransform(scrollYProgress, SCENES.map((_, i) => i / (SCENES.length - 1)), isBordo() ? SKY_BORDO : SKY_LIGHT);
  const domeTurn = useTransform(scrollYProgress, [0, 1], [0, 90]);
  useMotionValueEvent(scrollYProgress, 'change', (p) => {
    const mins = 19 * 60 + Math.round(Math.min(1, Math.max(0, p)) * 300);
    if (clockRef.current) {
      clockRef.current.textContent = `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
    }
  });


  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setScene(e.target.dataset.scene); });
    }, { rootMargin: '-50% 0px -50% 0px' });
    document.querySelectorAll('[data-scene]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  useEffect(() => { sound.scene(scene); }, [scene]);

  const toggleSound = () => {
    if (soundOn) { sound.disable(); setSoundOn(false); } else { sound.enable(); sound.scene(scene, true); setSoundOn(true); }
  };

  const view = useMemo(() => (data ? menuView(data, data.config?.liveMenuId) : []), [data]);
  const hideSoldOut = !!data?.config?.hideSoldOut;
  const carta = view
    .map((s) => ({ ...s, dishes: s.dishes.filter((d) => !hideSoldOut || !d.soldOut) }))
    .filter((s) => s.dishes.length);
  const footer = pick(data?.config?.i18n, lang, 'footer');

  return (
    <div className="ex" lang={lang}>
      <motion.div className="ex-sky" style={{ backgroundColor: sky }} aria-hidden="true" />

      <header className="ex-top">
        <Link to="/" className="ex-brand"><Logo /></Link>
        <div className="ex-clock" ref={clockRef} aria-hidden="true">19:00</div>
        <nav className="ex-top-right">
          <div className="ex-langs">
            {LANGS.map((l) => (
              <button key={l} type="button" aria-pressed={l === lang} onClick={() => changeLanguage(l)}>{LANG_LABELS[l]}</button>
            ))}
          </div>
          <ThemeToggle className="ex-theme" />
          <a href="#rezervasyon" className="ex-btn ex-btn--small ex-btn--solid">{t.book}</a>
          <Link to="/menu" className="ex-btn ex-btn--small">{t.menu}</Link>
        </nav>
      </header>

      <button type="button" className={`ex-sound${soundOn ? ' is-on' : ''}`} onClick={toggleSound} aria-pressed={soundOn}>
        <span /><span /><span /><span />
        <em>{soundOn ? t.soundOn : t.soundOff}</em>
      </button>

      {/* 19:00 the tile wall */}
      <section className="ex-scene ex-hero" data-scene="dusk">
        <TileWall />
        <div className="ex-arch">
          <h1 className="ex-title"><Logo className="ex-logo" /></h1>
          <p className="ex-arch-sub">Bir akşam · Una noche</p>
          <p className="ex-arch-line">{t.tagline}</p>
          <div className="ex-scroll-hint">{t.scroll}<i /></div>
        </div>
      </section>

      {/* 21:30 the whole menu, section by section */}
      <section className="ex-scene ex-carta" data-scene="table" id="carta">
        <div className="ex-wrap">
          <div className="ex-carta-head" data-reveal>
            <Chapter id="table" time="20:00" />
            <h2 className="ex-h1">{t.tableH}</h2>
            {pick(data?.config?.i18n, lang, 'date') && <p className="ex-carta-date">{pick(data?.config?.i18n, lang, 'date')}</p>}
          </div>
          {data && carta.length > 0 && <PrintSheet data={data} lang={lang} sections={carta} fluid />}
        </div>
      </section>

      <Band seed={3} />

      <Reservation lang={lang} />

      {/* 23:00 bar */}
      <section className="ex-scene ex-bar" data-scene="bar">
        <div className="ex-wrap ex-bar-in" data-reveal>
          <Glass className="ex-glass" />
          <div>
            <Chapter id="bar" time="23:00" />
            <h2 className="ex-display ex-display--gold">{t.barH}</h2>
          </div>
        </div>
      </section>

      <Band seed={2} />

      {/* 00:00 night under a turning dome */}
      <section className="ex-scene ex-night" data-scene="night">
        <motion.div className="ex-dome" style={{ rotate: domeTurn }}><Rosette color="#1f3a6b" /></motion.div>
        <div className="ex-night-in" data-reveal>
          <Chapter id="night" time="00:00" />
          <h2 className="ex-h1">{t.nightH}</h2>
          {footer && <p className="ex-p">{footer}</p>}
          <Link to="/menu" className="ex-btn ex-btn--solid">{t.nightCta}</Link>
          <p className="ex-sign"><Logo className="ex-logo-sign" /></p>
        </div>
      </section>
    </div>
  );
};

export default Experience;
